import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, TrendingUp, Send, MessageCircle, Calendar, ClipboardList, X, Save,
} from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function StudentReports() {
  const [reports, setReports] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState("all");
  const [form, setForm] = useState({
    task_title: "", progress_percent: 25, challenges: "", actions_taken: "", week_of: todayISO(),
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/periodic-reports/mine");
      setReports(data.reports || []);
      setComments(data.faculty_comments || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const facultyNames = [...new Set(comments.map((comment) => comment.faculty_name).filter(Boolean))];
  const visibleComments = selectedFaculty === "all"
    ? comments
    : comments.filter((comment) => comment.faculty_name === selectedFaculty);

  const submit = async () => {
    if (!form.task_title.trim()) { toast.error("عنوان المهمة مطلوب"); return; }
    setSaving(true);
    try {
      await api.post("/periodic-reports", form);
      toast.success("تم إرسال التقرير لمشرفك الأكاديمي (عضو هيئة التدريس)");
      setShowForm(false);
      setForm({ task_title: "", progress_percent: 25, challenges: "", actions_taken: "", week_of: todayISO() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div data-testid="student-reports">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التدريب التعاوني</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">تقاريري الأسبوعية</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              أرسل كل أسبوع تقريراً موجزاً لمشرفك الأكاديمي (عضو هيئة التدريس): عنوان المهمة، ما تم إنجازه، التحديات، وكيف تعاملت معها.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
            data-testid="new-report-btn"
          >
            <Plus className="h-5 w-5" /> تقرير جديد
          </button>
        </div>

        {/* Faculty comments (from supervisor) */}
        {comments.length > 0 && (
          <section className="mb-8" data-testid="faculty-comments-section">
            <div className="mb-3 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-teal-700" />
              <h2 className="font-heading font-black text-slate-900">توجيهات المشرف الأكاديمي (عضو هيئة التدريس)</h2>
              <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold arabic-numerals">{comments.length}</span>
              {facultyNames.length > 1 && (
                <select value={selectedFaculty} onChange={(event) => setSelectedFaculty(event.target.value)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm" aria-label="اختيار عضو هيئة التدريس" data-testid="faculty-member-select">
                  <option value="all">جميع أعضاء هيئة التدريس</option>
                  {facultyNames.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              )}
            </div>
            <div className="space-y-2">
              {visibleComments.map((c) => (
                <div key={c.id} className="p-4 rounded-2xl bg-cream-50 border border-cream-200" data-testid={`fac-comment-${c.id}`}>
                  <div className="text-xs text-slate-500 mb-1"><span className="font-bold text-teal-700">{c.faculty_name}</span> · {fmtDate(c.created_at)}</div>
                  <div className="text-sm text-slate-800 whitespace-pre-line">{c.body}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reports list */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-teal-700" />
            <h2 className="font-heading font-black text-slate-900">تقاريري السابقة</h2>
            <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold arabic-numerals">{reports.length}</span>
          </div>
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
          ) : reports.length === 0 ? (
            <div className="p-5 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
              لم ترفع أي تقرير بعد — ابدأ برفع تقرير هذا الأسبوع.
            </div>
          ) : (
            <div className="space-y-3" data-testid="reports-list">
              {reports.map((r) => (
                <div key={r.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`sreport-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-slate-900">{r.task_title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" /> الأسبوع: {fmtDate(r.week_of)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold arabic-numerals">
                      <TrendingUp className="h-3.5 w-3.5" /> {r.progress_percent}%
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-gradient-to-l from-teal-500 to-teal-700" style={{ width: `${Math.max(0, Math.min(100, r.progress_percent || 0))}%` }} />
                  </div>
                  {r.challenges && (
                    <div className="mt-3 rounded-lg bg-rose-50 border border-rose-100 p-3 text-sm">
                      <div className="text-[11px] font-bold text-rose-700 mb-1">التحديات</div>
                      <div className="text-slate-800 whitespace-pre-line">{r.challenges}</div>
                    </div>
                  )}
                  {r.actions_taken && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm">
                      <div className="text-[11px] font-bold text-emerald-700 mb-1">كيف تعاملت معها</div>
                      <div className="text-slate-800 whitespace-pre-line">{r.actions_taken}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {showForm && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="new-report-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 text-lg">تقرير أسبوعي جديد</h3>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                <Field label="عنوان المهمة">
                  <input value={form.task_title} onChange={(e) => setForm({ ...form, task_title: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" placeholder="مثال: بناء صفحة الفريق داخل الموقع" data-testid="report-title" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="أسبوع التقرير">
                    <input type="date" value={form.week_of} onChange={(e) => setForm({ ...form, week_of: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm arabic-numerals" data-testid="report-week" />
                  </Field>
                  <Field label={`نسبة الإنجاز — ${form.progress_percent}%`}>
                    <input type="range" min={0} max={100} step={5} value={form.progress_percent} onChange={(e) => setForm({ ...form, progress_percent: Number(e.target.value) })} className="w-full accent-teal-700" data-testid="report-progress" />
                  </Field>
                </div>
                <Field label="التحديات التي واجهتها">
                  <textarea rows={3} value={form.challenges} onChange={(e) => setForm({ ...form, challenges: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" placeholder="اذكر أبرز العقبات..." data-testid="report-challenges" />
                </Field>
                <Field label="كيف تعاملت معها">
                  <textarea rows={3} value={form.actions_taken} onChange={(e) => setForm({ ...form, actions_taken: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" placeholder="ما الإجراءات التي اتخذتها لتجاوزها؟" data-testid="report-actions" />
                </Field>
              </div>
              <div className="px-4 py-3 border-t border-slate-100 flex gap-2 justify-end">
                <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-report-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال التقرير
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
